import React, { useEffect, useMemo, useState } from 'react';
import './CollapsibleTopPanel.css';

const DEFAULT_BREAKPOINT = 640;

function isMobileViewport(breakpointPx) {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(`(max-width: ${breakpointPx}px)`).matches;
}

/**
 * Mobile-only collapsible wrapper for "page intro + filters".
 * - Mobile: shows a small toggle bar, content collapses/expands
 * - Desktop: always shows content, no toggle bar
 */
const CollapsibleTopPanel = ({
  children,
  breakpointPx = DEFAULT_BREAKPOINT,
  defaultOpenOnMobile = false,
  openLabel = 'Hide page info & filters',
  closedLabel = 'Show page info & filters',
  id = 'collapsible-top-panel',
  className = '',
}) => {
  const initialIsMobile = useMemo(() => isMobileViewport(breakpointPx), [breakpointPx]);
  const [isMobile, setIsMobile] = useState(initialIsMobile);
  const [open, setOpen] = useState(() => (initialIsMobile ? defaultOpenOnMobile : true));

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const media = window.matchMedia(`(max-width: ${breakpointPx}px)`);
    const sync = () => {
      const nextIsMobile = media.matches;
      setIsMobile(nextIsMobile);
      // Ensure desktop is always expanded.
      setOpen((prev) => (nextIsMobile ? prev : true));
    };
    sync();
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', sync);
      return () => media.removeEventListener('change', sync);
    }
    media.addListener(sync);
    return () => media.removeListener(sync);
  }, [breakpointPx]);

  const showContent = !isMobile || open;

  return (
    <div className={`ctp ${className}`.trim()}>
      {isMobile && (
        <button
          type="button"
          className="ctp__toggle"
          onClick={() => setOpen((p) => !p)}
          aria-expanded={open}
          aria-controls={id}
        >
          <span className="ctp__toggle-text">{open ? openLabel : closedLabel}</span>
          <span className={`ctp__toggle-icon ${open ? 'is-open' : ''}`}>▾</span>
        </button>
      )}

      {showContent && (
        <div id={id} className="ctp__content">
          {children}
        </div>
      )}
    </div>
  );
};

export default CollapsibleTopPanel;

